import { FC, useState } from 'react';
import { formatCurrencyCart, splitCurrencyCart } from '../../utils/formatters';
import { translate } from '../../i18n';
import { Spinner } from '../Skeleton';
import { isDesktop } from '../../platform';
import type { PaymentTotals } from '../../domain/models';
import closeIcon from '../../assets/icons/close.svg';
import tabFoldedSvg from '../../assets/footer/tab-folded.svg';
import tabUnfoldedSvg from '../../assets/footer/tab-unfolded.svg';
import chevronDownSvg from '../../assets/footer/chevron-down.svg';
import { checkoutTotalFooterStyles as styles } from './styles';

export interface CheckoutTotalFooterProps {
  total: number;
  /** Desglose del pedido (`totals` de pre-initiate). Sin él no se muestra "Ver detalle". */
  summary?: PaymentTotals;
  onSubmit?: () => void;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
  /** `false` cuando el iframe crece con el contenido y scrollea la página de afuera. */
  pinned?: boolean;
}

/** Monto con los centavos como superíndice, como en el diseño. */
const Amount: FC<{ value: number; wholeStyle: React.CSSProperties; centsStyle: React.CSSProperties; testID?: string }> = ({
  value,
  wholeStyle,
  centsStyle,
  testID,
}) => {
  const { whole, cents } = splitCurrencyCart(value);
  const accessibleLabel = formatCurrencyCart(value);
  return (
    <span style={styles.amount} data-testid={testID}>
      <span style={styles.visuallyHidden}>{accessibleLabel}</span>
      <span style={wholeStyle} aria-hidden="true">{whole}</span>
      <span style={centsStyle} aria-hidden="true">{cents}</span>
    </span>
  );
};

/**
 * CheckoutTotalFooter — Total + botón "Finalizar compra", con resumen del pedido plegable
 * ("Ver detalle" / "Cerrar detalle").
 */
export const CheckoutTotalFooter: FC<CheckoutTotalFooterProps> = ({
  total,
  summary,
  onSubmit,
  disabled = false,
  loading = false,
  testID = 'checkout-total-footer',
  pinned = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const showDetail = Boolean(summary) && expanded;

  const totalRow = (
    <div style={styles.totalRow}>
      <span style={styles.totalLabel}>{translate('paymentsUI.footer.total')}</span>
      <Amount
        value={total}
        wholeStyle={styles.totalWhole}
        centsStyle={styles.totalCents}
        testID={`${testID}-total-amount`}
      />
    </div>
  );

  return (
    <div style={pinned ? styles.container : styles.flow} data-testid={testID}>
      {summary && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          data-testid={`${testID}-toggle`}
          style={{ ...styles.tab, ...(expanded ? styles.tabUnfolded : styles.tabFolded) }}
        >
          <img
            src={expanded ? tabUnfoldedSvg : tabFoldedSvg}
            alt=""
            style={styles.tabBackground}
          />
          <span style={styles.tabLabel}>
            {translate(expanded ? 'paymentsUI.footer.hideDetail' : 'paymentsUI.footer.showDetail')}
          </span>
        </button>
      )}

      {showDetail && summary ? (
        <div data-testid={`${testID}-summary`} style={styles.summary}>
          <div style={styles.summaryHeader}>
            <span style={styles.summaryTitle}>{translate('paymentsUI.footer.summaryTitle')}</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label={translate('paymentsUI.footer.close')}
              style={styles.closeButton}
            >
              <img src={closeIcon} alt="" width={24} height={24} />
            </button>
          </div>
          <div style={styles.summaryRows}>
            {/* TODO(ENA-1712): mostrar "(N artículos)"; pre-initiate no trae la cantidad. */}
            <div style={styles.summaryRow}>
              <span style={styles.summaryText}>{translate('paymentsUI.footer.products')}</span>
              <Amount value={summary.items} wholeStyle={styles.summaryText} centsStyle={styles.summaryCents} />
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.summaryText}>{translate('paymentsUI.footer.shipping')}</span>
              <Amount value={summary.shipping} wholeStyle={styles.summaryText} centsStyle={styles.summaryCents} />
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.discountLabel}>
                {translate('paymentsUI.footer.discounts')}
                <img src={chevronDownSvg} alt="" width={12} height={12} />
              </span>
              <Amount
                value={Math.abs(summary.discounts)}
                wholeStyle={styles.discountAmount}
                centsStyle={styles.discountCents}
              />
            </div>
          </div>
          <div style={styles.divider} />
          {totalRow}
        </div>
      ) : (
        totalRow
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || loading}
        data-testid={`${testID}-submit`}
        style={{
          ...styles.submitButton,
          ...((disabled || loading) && styles.submitButtonDisabled),
        }}
      >
        {loading && !isDesktop && <Spinner size={18} />}
        {loading ? translate('paymentsUI.footer.submitting') : translate('paymentsUI.footer.submit')}
      </button>
    </div>
  );
};
